import { useEffect, useMemo, useState } from "react";
import { useGetIdentity, usePermissions } from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";

const defaultNormalizeFilter = (filter) => filter;

const useCompanyScopedListFilters = (
  defaultFilterValues = {},
  normalizeFilter = defaultNormalizeFilter
) => {
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const [liveCompanyId, setLiveCompanyId] = useState(identity?.companyID || null);

  const canSeeCompanyColumn =
    permissions?.administrator ||
    permissions?.superadmin ||
    permissions?.update_company;

  const currentCompanyFilter = useMemo(
    () => (liveCompanyId ? { company_id: liveCompanyId } : {}),
    [liveCompanyId]
  );

  useEffect(() => {
    if (identity?.companyID && identity.companyID !== liveCompanyId) {
      setLiveCompanyId(identity.companyID);
    }
    if (!identity?.companyID && liveCompanyId) {
      setLiveCompanyId(null);
    }
  }, [identity?.companyID, liveCompanyId]);

  useEffect(() => {
    const handler = (event) => {
      const nextCompanyId = event?.detail?.companyID || null;
      setLiveCompanyId((prev) => (prev === nextCompanyId ? prev : nextCompanyId));
    };

    window.addEventListener("authIdentityUpdated", handler);
    return () => window.removeEventListener("authIdentityUpdated", handler);
  }, []);

  const locationFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    try {
      return normalizeFilter(JSON.parse(searchParams.get("filter") || "{}"));
    } catch {
      return {};
    }
  }, [location.search, normalizeFilter]);

  useEffect(() => {
    if (!currentCompanyFilter.company_id) return;
    if (locationFilter.company_id === currentCompanyFilter.company_id) return;

    const searchParams = new URLSearchParams(location.search);
    const nextFilter = {
      ...defaultFilterValues,
      ...locationFilter,
      company_id: currentCompanyFilter.company_id,
    };
    searchParams.set("filter", JSON.stringify(nextFilter));

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
  }, [
    currentCompanyFilter.company_id,
    defaultFilterValues,
    location.pathname,
    location.search,
    locationFilter,
    navigate,
  ]);

  const mergedFilterDefaultValues = useMemo(
    () => ({
      ...defaultFilterValues,
      ...currentCompanyFilter,
    }),
    [currentCompanyFilter, defaultFilterValues]
  );

  return {
    permissions,
    canSeeCompanyColumn,
    currentCompanyFilter,
    filterDefaultValues: mergedFilterDefaultValues,
    locationFilter,
  };
};

export default useCompanyScopedListFilters;
